<script setup lang="ts">
import { ChevronRight, Plus } from "@lucide/vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { Button } from "@/shared/ui";

import type { Row } from "../model/store";
import { useServicesCatalogStore } from "../model/store";
import AvatarStack from "./AvatarStack.vue";

/**
 * V3: єдина tap-target (spec §7.1, 168-T3 / 187-T2).
 * Saved row: `<li role="button" tabindex="0">` → emit open.
 * PendingCreate row: не інтерактивний, retry/dismiss при помилці.
 * Внутрішні декоративні елементи: `pointer-events: none` + `aria-hidden`.
 */
const props = defineProps<{ row: Row }>();
const emit = defineEmits<{
  open: [serviceId: string];
  removed: [];
}>();

const { t } = useI18n();
const store = useServicesCatalogStore();

const ariaLabel = computed(() => {
  if (props.row.kind !== "saved") return undefined;
  const { name, assignedMasterCount } = props.row.service;
  return assignedMasterCount > 0
    ? `${name}, ${t("services.assignedCount", { count: assignedMasterCount })}`
    : `${name}, ${t("services.detail.addMaster")}`;
});

function onActivate(): void {
  if (props.row.kind === "saved") {
    emit("open", props.row.service.id);
  }
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onActivate();
  }
}

function onRetry(): void {
  if (props.row.kind === "pendingCreate") {
    store.retryCreate(props.row.tempId);
  }
}

function onDismiss(): void {
  if (props.row.kind === "pendingCreate") {
    store.dismissCreate(props.row.tempId);
    emit("removed");
  }
}
</script>

<template>
  <!-- PendingCreate row: non-interactive, shows retry/dismiss on error -->
  <li v-if="row.kind === 'pendingCreate'" class="service-row service-row--pending">
    <span class="service-row__name" aria-hidden="true">{{ row.name }}</span>
    <span
      v-if="row.mode === 'pending'"
      class="service-row__saving"
      aria-hidden="true"
    >{{ t("services.saving") }}</span>
    <template v-if="row.mode === 'error'">
      <span class="service-row__error">{{ t(row.errorKey ?? "services.errors.invalidName") }}</span>
      <Button
        size="sm"
        variant="ghost"
        class="service-row__retry"
        @click="onRetry"
      >{{ t("services.actions.retry") }}</Button>
      <Button
        size="sm"
        variant="ghost"
        class="service-row__dismiss"
        @click="onDismiss"
      >{{ t("services.actions.dismiss") }}</Button>
    </template>
  </li>

  <!-- Saved row: single tap-target, V3 -->
  <li
    v-else
    role="button"
    tabindex="0"
    class="service-row service-row--saved"
    :aria-label="ariaLabel"
    @click="onActivate"
    @keydown="onKeydown"
  >
    <span class="service-row__name" aria-hidden="true">{{ row.service.name }}</span>
    <AvatarStack
      v-if="row.service.assignedMasterCount > 0"
      :preview="store.assignedPreview(row.service.id)"
      :count="row.service.assignedMasterCount"
      class="service-row__avatars"
      aria-hidden="true"
    />
    <span
      v-else
      class="service-row__no-masters"
      aria-hidden="true"
    >
      <Plus :size="14" aria-hidden="true" />{{ t("services.row.addMaster") }}
    </span>
    <ChevronRight class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
  </li>
</template>

<style scoped>
.service-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
  list-style: none;
  cursor: default;
}

.service-row--saved {
  cursor: pointer;
}

.service-row--saved:focus-visible {
  outline: 2px solid var(--ring);
  outline-offset: -2px;
}

.service-row--saved:active {
  background-color: var(--accent);
}

.service-row__name {
  flex: 1;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  pointer-events: none;
}

.service-row__avatars {
  flex-shrink: 0;
  pointer-events: none;
}

.service-row__no-masters {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding-inline: 0.625rem;
  height: 1.5rem;
  border-radius: var(--radius);
  border: 1px dashed var(--primary);
  color: var(--primary);
  font-size: 0.75rem;
  pointer-events: none;
}

.service-row__saving {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  pointer-events: none;
}

.service-row__error {
  font-size: 0.75rem;
  color: var(--destructive);
}

.service-row__retry,
.service-row__dismiss {
  flex-shrink: 0;
}
</style>
